import React, { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Category, Product } from '../types';
import ProductCard from '../components/ProductCard';
import ProductTable from '../components/ProductTable';
import SearchBox from '../components/SearchBox';
import SEO from '../components/SEO';
import LoadingSpinner from '../components/LoadingSpinner';
import useMediaQuery from "../hooks/useMediaQuery";
import { useFavorites } from '../hooks/useFavorites';
import { fetchProducts, fetchCategories } from '../services/api';
import { hasRealImage } from '../utils/images';

interface Filters {
  categories: string[];
  minRating?: number;
  minHealthScore?: number;
  query?: string;
  favoritesOnly: boolean;
}

const SearchPage: React.FC = () => {
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const { favorites } = useFavorites();

  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [categoryList, setCategoryList] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortOption, setSortOption] = useState<string>(queryParams.get('sort') || 'rating');
  const [view, setView] = useState<'cards' | 'table'>('cards');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const isMobile = useMediaQuery('(max-width: 1023px)');

  const [filters, setFilters] = useState<Filters>({
    categories: queryParams.get('category') ? [queryParams.get('category')!] : [],
    minRating: queryParams.get('rating') ? Number(queryParams.get('rating')) : undefined,
    minHealthScore: queryParams.get('health') ? Number(queryParams.get('health')) : undefined,
    query: queryParams.get('q') || undefined,
    favoritesOnly: queryParams.get('saved') === '1',
  });

  // Category tree + slug -> descendant-slugs map for inclusive filtering
  const { roots, childrenOf, descendantsOf } = useMemo(() => {
    const childrenOf = new Map<number | string, Category[]>();
    const roots: Category[] = [];
    for (const c of categoryList) {
      const pid = c.parent_id ?? c.parentId ?? null;
      if (pid == null) roots.push(c);
      else {
        if (!childrenOf.has(pid)) childrenOf.set(pid, []);
        childrenOf.get(pid)!.push(c);
      }
    }
    const descendantsOf = new Map<string, Set<string>>();
    const collect = (c: Category, acc: Set<string>) => {
      acc.add(c.slug);
      (childrenOf.get(c.id) || []).forEach((ch) => collect(ch, acc));
    };
    for (const c of categoryList) {
      const acc = new Set<string>();
      collect(c, acc);
      descendantsOf.set(c.slug, acc);
    }
    return { roots, childrenOf, descendantsOf };
  }, [categoryList]);

  useEffect(() => {
    (async () => {
      const data = await fetchCategories();
      if (data && data.length > 0) setCategoryList(data);
    })();
  }, []);

  // React to URL changes (e.g. header search navigation)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setFilters({
      categories: queryParams.get('category') ? [queryParams.get('category')!] : [],
      minRating: queryParams.get('rating') ? Number(queryParams.get('rating')) : undefined,
      minHealthScore: queryParams.get('health') ? Number(queryParams.get('health')) : undefined,
      query: queryParams.get('q') || undefined,
      favoritesOnly: queryParams.get('saved') === '1',
    });
    setSortOption(queryParams.get('sort') || 'rating');
  }, [location.search, queryParams]);

  // Fetch whenever the text query changes; everything else filters client-side.
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const products = await fetchProducts({ query: filters.query, limit: 1000 });
        setAllProducts(products);
      } catch {
        setAllProducts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [filters.query]);

  const results = useMemo(() => {
    let list = allProducts;

    if (filters.categories.length > 0) {
      const allowed = new Set<string>();
      filters.categories.forEach((slug) => {
        (descendantsOf.get(slug) || new Set([slug])).forEach((s) => allowed.add(s));
      });
      list = list.filter((p) => p.category && allowed.has(p.category));
    }
    if (filters.minRating) list = list.filter((p) => p.rating >= filters.minRating!);
    if (filters.minHealthScore) list = list.filter((p) => p.healthScore >= filters.minHealthScore!);
    if (filters.favoritesOnly) list = list.filter((p) => favorites.includes(String(p.id)));

    const sorted = [...list].sort((a, b) => {
      const imgDiff = Number(hasRealImage(b)) - Number(hasRealImage(a));
      if (imgDiff !== 0) return imgDiff;
      switch (sortOption) {
        case 'saved': return (b.saves || 0) - (a.saves || 0);
        case 'health': return b.healthScore - a.healthScore;
        case 'price-low': return a.price - b.price;
        case 'price-high': return b.price - a.price;
        case 'name': return a.name.localeCompare(b.name);
        default: return b.rating - a.rating;
      }
    });
    return sorted;
  }, [allProducts, filters, favorites, sortOption, descendantsOf]);

  const syncUrl = (f: Filters, sort: string) => {
    const params = new URLSearchParams();
    if (f.query) params.set('q', f.query);
    if (f.categories.length === 1) params.set('category', f.categories[0]);
    if (f.minRating) params.set('rating', String(f.minRating));
    if (f.minHealthScore) params.set('health', String(f.minHealthScore));
    if (f.favoritesOnly) params.set('saved', '1');
    if (sort) params.set('sort', sort);
    window.history.pushState({}, '', `${location.pathname}${params.toString() ? '?' + params.toString() : ''}`);
  };

  const updateFilters = (patch: Partial<Filters>) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    syncUrl(next, sortOption);
  };

  const toggleCategory = (slug: string) => {
    const next = filters.categories.includes(slug)
      ? filters.categories.filter((s) => s !== slug)
      : [...filters.categories, slug];
    updateFilters({ categories: next });
  };

  const toggleExpanded = (slug: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const clearFilters = () => {
    const next: Filters = { categories: [], query: filters.query, favoritesOnly: false };
    setFilters(next);
    syncUrl(next, sortOption);
  };

  const handleSortChange = (option: string) => {
    setSortOption(option);
    syncUrl(filters, option);
  };

  const hasActiveFilters =
    filters.categories.length > 0 || filters.minRating || filters.minHealthScore || filters.favoritesOnly;

  const filtersVisible = !isMobile || isFiltersOpen;

  const categoryLabel = (slug: string) =>
    categoryList.find((c) => c.slug === slug)?.name || slug;

  const renderCategoryNode = (cat: Category, depth: number) => {
    const kids = childrenOf.get(cat.id) || [];
    const isOpen = expanded.has(cat.slug) || filters.categories.includes(cat.slug);
    const checked = filters.categories.includes(cat.slug);
    return (
      <div key={cat.id} style={{ paddingLeft: depth * 14 }}>
        <div className="flex items-center gap-1.5 py-1">
          {kids.length > 0 ? (
            <button
              type="button"
              onClick={() => toggleExpanded(cat.slug)}
              aria-label={isOpen ? `Collapse ${cat.name}` : `Expand ${cat.name}`}
              className="h-5 w-5 flex items-center justify-center text-earth-400 hover:text-earth-600"
            >
              <svg className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-90' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.293 4.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414-1.414L11.586 10 7.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          ) : (
            <span className="w-5" />
          )}
          <label className="flex items-center gap-2 cursor-pointer text-sm text-earth-700 hover:text-earth-900">
            <input
              type="checkbox"
              checked={checked}
              onChange={() => toggleCategory(cat.slug)}
              className="h-4 w-4 accent-sage-600"
            />
            <span className={depth === 0 ? 'font-medium' : ''}>{cat.name}</span>
          </label>
        </div>
        {isOpen && kids.map((k) => renderCategoryNode(k, depth + 1))}
      </div>
    );
  };

  return (
    <div className="bg-cream-100 min-h-screen py-8">
      <SEO
        title={filters.query
          ? `Search results for "${filters.query}"`
          : filters.categories.length > 0
            ? `${filters.categories.map(categoryLabel).join(', ')} Products`
            : 'Browse All Products'}
        description={`Discover health-conscious products${filters.categories.length ? ` in ${filters.categories.map(categoryLabel).join(', ')}` : ''}${filters.query ? ` matching "${filters.query}"` : ''}.`}
        url={`https://goodcart.org/search${location.search}`}
      />

      <div className="container-custom">
        <div className="mb-6 max-w-xl">
          <SearchBox initialQuery={filters.query || ''} large />
        </div>

        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-display text-earth-900">
            {filters.query
              ? `Results for “${filters.query}”`
              : filters.categories.length > 0
                ? filters.categories.map(categoryLabel).join(', ')
                : filters.favoritesOnly
                  ? 'Your saved picks'
                  : 'All products'}
          </h1>
          <p className="text-earth-600 text-sm mt-1">{results.length} products</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Filters */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-earth-100 sticky top-20 mb-6">
              {isMobile && (
                <button
                  onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                  className="w-full p-4 flex items-center justify-between font-semibold text-earth-900 focus:outline-none"
                  aria-expanded={isFiltersOpen}
                  aria-controls="filter-panel"
                >
                  <span>Filters</span>
                  <svg className={`h-5 w-5 transition-transform duration-300 ${isFiltersOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              )}

              {filtersVisible && (
                <div className="p-5" id="filter-panel">
                  <div className="flex items-center justify-between mb-5">
                    {!isMobile && <h2 className="font-semibold text-earth-900">Filters</h2>}
                    {hasActiveFilters && (
                      <button onClick={clearFilters} className="text-sm text-sage-700 hover:text-sage-800">
                        Clear all
                      </button>
                    )}
                  </div>

                  {/* Saved filter */}
                  <div className="mb-5">
                    <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-earth-800">
                      <input
                        type="checkbox"
                        checked={filters.favoritesOnly}
                        onChange={(e) => updateFilters({ favoritesOnly: e.target.checked })}
                        className="h-4 w-4 accent-sage-600"
                      />
                      My saved picks
                    </label>
                  </div>

                  {/* Nested category tree */}
                  <div className="mb-5">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-earth-500 mb-2">Categories</h3>
                    <div className="max-h-72 overflow-y-auto pr-1">
                      {roots.map((r) => renderCategoryNode(r, 0))}
                    </div>
                  </div>

                  {/* Rating */}
                  <div className="mb-5">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-earth-500 mb-2">Minimum rating</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {[4.5, 4, 3.5, 3].map((rating) => (
                        <button
                          key={rating}
                          type="button"
                          onClick={() =>
                            updateFilters({ minRating: filters.minRating === rating ? undefined : rating })
                          }
                          aria-pressed={filters.minRating === rating}
                          className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                            filters.minRating === rating
                              ? 'bg-sage-600 text-cream-50 border-sage-600'
                              : 'border-earth-200 text-earth-700 hover:border-sage-400'
                          }`}
                        >
                          {rating}+ ★
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Health score */}
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-earth-500 mb-2">Minimum health score</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {[90, 80, 70].map((score) => (
                        <button
                          key={score}
                          type="button"
                          onClick={() =>
                            updateFilters({ minHealthScore: filters.minHealthScore === score ? undefined : score })
                          }
                          aria-pressed={filters.minHealthScore === score}
                          className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                            filters.minHealthScore === score
                              ? 'bg-sage-600 text-cream-50 border-sage-600'
                              : 'border-earth-200 text-earth-700 hover:border-sage-400'
                          }`}
                        >
                          {score}+
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Results */}
          <div className="lg:col-span-3">
            <div className="flex justify-between items-center mb-5 gap-3 flex-wrap">
              <div className="inline-flex rounded-full border border-earth-200 bg-white p-0.5" role="group" aria-label="View">
                <button
                  type="button"
                  onClick={() => setView('cards')}
                  aria-pressed={view === 'cards'}
                  className={`px-3.5 py-1.5 text-sm rounded-full transition-colors ${view === 'cards' ? 'bg-sage-600 text-cream-50' : 'text-earth-600 hover:text-earth-800'}`}
                >
                  Cards
                </button>
                <button
                  type="button"
                  onClick={() => setView('table')}
                  aria-pressed={view === 'table'}
                  className={`px-3.5 py-1.5 text-sm rounded-full transition-colors ${view === 'table' ? 'bg-sage-600 text-cream-50' : 'text-earth-600 hover:text-earth-800'}`}
                >
                  Table
                </button>
              </div>
              <label className="flex items-center gap-2 text-sm text-earth-600">
                Sort by
                <select
                  className="text-sm border border-earth-200 rounded-full px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-sage-500"
                  value={sortOption}
                  onChange={(e) => handleSortChange(e.target.value)}
                >
                  <option value="rating">Highest rated</option>
                  <option value="saved">Most saved</option>
                  <option value="health">Health score</option>
                  <option value="price-low">Price: low to high</option>
                  <option value="price-high">Price: high to low</option>
                  <option value="name">Name</option>
                </select>
              </label>
            </div>

            {loading ? (
              <div className="flex justify-center py-16"><LoadingSpinner /></div>
            ) : results.length === 0 ? (
              <div className="bg-white rounded-xl border border-earth-100 p-10 text-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-14 w-14 mx-auto text-earth-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-4.35-4.35M17 10.5a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z" />
                </svg>
                <h3 className="text-lg font-medium text-earth-900 mb-2">No products found</h3>
                <p className="text-earth-600 mb-6">Try a different search or loosen the filters.</p>
                <button onClick={clearFilters} className="btn btn-primary">Clear filters</button>
              </div>
            ) : view === 'cards' ? (
              <div className="grid grid-cols-2 xl:grid-cols-3 gap-5 md:gap-6">
                {results.map((product, index) => (
                  <ProductCard key={product.id} product={product} index={index} />
                ))}
              </div>
            ) : (
              <ProductTable products={results} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchPage;
